import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validatePassword } from "../../customer-util";
import PasswordRequirementsBox from "../components/password-requirement-box";
import Input from "../../../../common/ekedc-components/input/Input";
import styles from "./forgot-password.module.scss";

interface FormData {
	password: string;
	confirmPassword: string;
}

interface ChangePasswordFormProps {
	onSubmit: (password: string) => void | Promise<void>;
}

export const CHANGE_PASSWORD_FORM_ID = "forgot-password-change-form";

export const ChangePasswordForm = ({ onSubmit }: ChangePasswordFormProps) => {
	const { t } = useTranslation();
	const {
		control,
		handleSubmit,
		formState: { errors },
		watch,
	} = useForm<FormData>({
		mode: "onChange",
		defaultValues: { password: "", confirmPassword: "" },
	});

	const password = watch("password");

	const handleOnSubmit = (data: FormData) => onSubmit(data.password);

	return (
		<div className={styles.card}>
			<div className={styles.cardHeader}>
				<p className={styles.cardTitle}>{t("common.labels.createNewPasswordTitle")}</p>
				<p className={styles.cardSubtitle}>{t("common.labels.createNewPasswordSubtitle")}</p>
			</div>
			<form
				id={CHANGE_PASSWORD_FORM_ID}
				className={styles.formFields}
				onSubmit={handleSubmit(handleOnSubmit)}
				noValidate
			>
				<Controller
					name="password"
					control={control}
					rules={{
						required: t("corporate.passwordRequired"),
						minLength: { value: 10, message: t("auth.web.passwordMinLength") },
						maxLength: { value: 64, message: t("auth.web.passwordMaxLength") },
						validate: value => validatePassword(value),
					}}
					render={({ field }) => (
						<Input
							{...field}
							label={t("common.labels.newPassword")}
							type="password"
							placeholder={t("auth.web.enterYourNewPassword")}
							error={errors.password?.message}
						/>
					)}
				/>

				<Controller
					name="confirmPassword"
					control={control}
					rules={{
						required: t("auth.web.pleaseConfirmPassword"),
						validate: value => value === password || t("common.validation.passwordsDoNotMatch"),
					}}
					render={({ field }) => (
						<Input
							{...field}
							label={t("common.labels.confirmPassword")}
							type="password"
							placeholder={t("auth.web.reenterYourNewPassword")}
							error={errors.confirmPassword?.message}
						/>
					)}
				/>
			</form>
			{password && <PasswordRequirementsBox password={password} />}
		</div>
	);
};
